import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase-config';

const TIMEOUT_MINUTES = 5;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
const THROTTLE_MS = 60_000;

/**
 * Auto-logout after inactivity. Merchant web uses a fixed 5-min timeout.
 * @param {boolean} isAuthenticated — only run when user is logged in
 */
export default function useSessionTimeout(isAuthenticated) {
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(0);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (_) {
      window.location.reload();
    }
  }, []);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    if (now - throttleRef.current < THROTTLE_MS) return;
    throttleRef.current = now;
    lastActivityRef.current = now;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(handleLogout, TIMEOUT_MINUTES * 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    resetTimer();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= TIMEOUT_MINUTES * 60 * 1000) {
          handleLogout();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, resetTimer, handleLogout]);
}
